import React, { useCallback, useEffect, useMemo, useState } from "react";
import { userHasNamedTabAccess } from "../../lib/utils";

async function adminFetch(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    ...options,
  });
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const d = await res.json(); msg = d?.detail || JSON.stringify(d) || msg; } catch (_) {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Notice({ msg, type }) {
  if (!msg) return null;
  return <div className={`message-banner ${type}`}>{msg}</div>;
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ animation: "lj-spin 0.9s linear infinite", display: "inline-block", verticalAlign: "middle", marginRight: 6 }}
      strokeLinecap="round">
      <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
      <path d="M12 3a9 9 0 019 9" />
    </svg>
  );
}

// ── Sub-tab 1: Reassign Agent Leads ──────────────────────────────────────────

function ReassignAgentLeadsPanel({ agents }) {
  const [sourceAgentId, setSourceAgentId] = useState("");
  const [includePast, setIncludePast] = useState(false);
  const [citiesData, setCitiesData] = useState(null);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedCities, setSelectedCities] = useState([]);
  // city_assignments: { [city]: { method: "round_robin"|"all_to_one", agent_ids: [] } }
  const [cityAssignments, setCityAssignments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const sourceAgent = agents.find((a) => a.id === sourceAgentId);

  async function loadCities(agentId) {
    if (!agentId) { setCitiesData(null); setSelectedCities([]); setCityAssignments({}); return; }
    setLoadingCities(true);
    setError("");
    setResult(null);
    try {
      const data = await adminFetch(`/api/crm/admin/agent-cities/?agent_id=${agentId}`);
      setCitiesData(data.cities || []);
      // Pre-select all cities
      const allCities = (data.cities || []).map((c) => c.city);
      setSelectedCities(allCities);
      // Auto-fill city assignments
      const assignments = {};
      for (const c of data.cities || []) {
        const agents = c.available_agents || [];
        assignments[c.city] = {
          method: agents.length > 1 ? "round_robin" : "all_to_one",
          agent_ids: agents.length === 1 ? [agents[0].id] : [],
        };
      }
      setCityAssignments(assignments);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCities(false);
    }
  }

  function toggleCity(city) {
    setSelectedCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  }

  function updateAssignment(city, field, value) {
    setCityAssignments((prev) => ({
      ...prev,
      [city]: { ...prev[city], [field]: value },
    }));
  }

  function toggleAgentInCity(city, agentId) {
    const current = cityAssignments[city]?.agent_ids || [];
    const updated = current.includes(agentId)
      ? current.filter((id) => id !== agentId)
      : [...current, agentId];
    updateAssignment(city, "agent_ids", updated);
  }

  const visibleCities = (citiesData || []).filter((c) => selectedCities.includes(c.city));

  const canSubmit = useMemo(() => {
    if (!sourceAgentId || !visibleCities.length) return false;
    return visibleCities.every((c) => {
      const cfg = cityAssignments[c.city] || {};
      if (cfg.method === "round_robin") return c.available_agents.length > 1;
      return (cfg.agent_ids || []).length > 0;
    });
  }, [sourceAgentId, visibleCities, cityAssignments]);

  async function handleSubmit() {
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const city_assignments = visibleCities
        .filter((c) => cityAssignments[c.city])
        .map((c) => {
          const cfg = cityAssignments[c.city];
          const agent_ids =
            cfg.method === "round_robin"
              ? c.available_agents.map((a) => a.id)
              : cfg.agent_ids;
          return { city: c.city, method: cfg.method, agent_ids };
        });
      const data = await adminFetch("/api/crm/admin/reassign-agent-leads/", {
        method: "POST",
        body: JSON.stringify({
          source_agent_id: sourceAgentId,
          cities: selectedCities,
          include_past: includePast,
          city_assignments,
        }),
      });
      setResult(data);
      // Reset
      setSourceAgentId("");
      setCitiesData(null);
      setSelectedCities([]);
      setCityAssignments({});
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ras-panel">
      <Notice msg={error} type="error" />
      {result && (
        <div className="message-banner success">
          Reassigned <strong>{result.reassigned}</strong> lead{result.reassigned !== 1 ? "s" : ""} successfully.
          {result.by_city && Object.keys(result.by_city).length > 0 && (
            <span>
              {" "}({Object.entries(result.by_city).map(([city, n]) => `${city}: ${n}`).join(", ")})
            </span>
          )}
        </div>
      )}

      {/* Step 1 — Source agent */}
      <div className="ras-section">
        <h3 className="ras-section-title">1. Select Source Agent</h3>
        <div className="ras-row">
          <label className="field-stack" style={{ maxWidth: 320 }}>
            <span>Agent whose leads to reassign</span>
            <select
              value={sourceAgentId}
              onChange={(e) => {
                setSourceAgentId(e.target.value);
                loadCities(e.target.value);
              }}
            >
              <option value="">— Select agent —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name} ({a.username})</option>
              ))}
            </select>
          </label>
          <label className="ras-toggle-chip">
            <input
              type="checkbox"
              className="ras-cb-input"
              checked={includePast}
              onChange={(e) => setIncludePast(e.target.checked)}
            />
            <span className="ras-cb-box" />
            <span>Include past leads<span className="ras-toggle-sub"> Booked / Delivered / Lost</span></span>
          </label>
        </div>
      </div>

      {/* Step 2 — Cities */}
      {loadingCities && (
        <div className="ras-loading"><Spinner />Loading cities...</div>
      )}

      {citiesData && !loadingCities && (
        <>
          <div className="ras-section">
            <h3 className="ras-section-title">2. Select Cities to Reassign</h3>
            {citiesData.length === 0 ? (
              <p className="ras-empty">This agent has no leads to reassign.</p>
            ) : (
              <div className="ras-city-grid">
                {citiesData.map((c) => (
                  <label key={c.city} className="ras-city-chip">
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(c.city)}
                      onChange={() => toggleCity(c.city)}
                    />
                    <span>{c.city}</span>
                    <span className="ras-city-count">{c.lead_count} lead{c.lead_count !== 1 ? "s" : ""}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Step 3 — Distribution */}
          {visibleCities.length > 0 && (
            <div className="ras-section">
              <h3 className="ras-section-title">3. Configure Distribution</h3>
              <div className="ras-dist-list">
                {visibleCities.map((c) => {
                  const cfg = cityAssignments[c.city] || {};
                  const hasMultiple = c.available_agents.length > 1;
                  const hasNone = c.available_agents.length === 0;
                  return (
                    <div key={c.city} className="ras-dist-card">
                      <div className="ras-dist-header">
                        <strong>{c.city}</strong>
                        <span className="ras-dist-count">{c.lead_count} lead{c.lead_count !== 1 ? "s" : ""}</span>
                      </div>

                      {hasNone ? (
                        <p className="ras-dist-warn">No other agents are scoped to this city.</p>
                      ) : !hasMultiple ? (
                        <p className="ras-dist-single">
                          All leads → <strong>{c.available_agents[0].name}</strong>
                        </p>
                      ) : (
                        <>
                          <div className="ras-method-row">
                            <button
                              type="button"
                              className={`ras-method-btn${cfg.method === "round_robin" ? " active" : ""}`}
                              onClick={() => updateAssignment(c.city, "method", "round_robin")}
                            >
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 4H7a5 5 0 000 10h1M3 16h10a5 5 0 000-10h-1"/><path d="M14 2l3 2-3 2M6 18l-3-2 3-2"/></svg>
                              Round Robin
                            </button>
                            <button
                              type="button"
                              className={`ras-method-btn${cfg.method === "all_to_one" ? " active" : ""}`}
                              onClick={() => updateAssignment(c.city, "method", "all_to_one")}
                            >
                              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="10" cy="8" r="4"/><path d="M3 18c0-3.3 3.1-6 7-6s7 2.7 7 6"/></svg>
                              Specific agent(s)
                            </button>
                          </div>
                          {cfg.method === "all_to_one" && (
                            <div className="ras-agent-chips">
                              {c.available_agents.map((a) => (
                                <label
                                  key={a.id}
                                  className={`ras-agent-chip${(cfg.agent_ids || []).includes(a.id) ? " selected" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="ras-cb-input"
                                    checked={(cfg.agent_ids || []).includes(a.id)}
                                    onChange={() => toggleAgentInCity(c.city, a.id)}
                                  />
                                  {a.name}
                                </label>
                              ))}
                            </div>
                          )}
                          {cfg.method === "round_robin" && (
                            <div className="ras-agent-chips">
                              {c.available_agents.map((a) => (
                                <span key={a.id} className="ras-agent-chip selected static">{a.name}</span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="ras-actions">
            <button
              type="button"
              className="primary-button"
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
            >
              {submitting ? <><Spinner />Reassigning...</> : `Reassign ${selectedCities.length} city leads`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-tab 2: Reassign Leads ─────────────────────────────────────────────────

const PAGE_SIZE = 15;

function ReassignLeadsPanel({ agents }) {
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetAgentId, setTargetAgentId] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const fetchLeads = useCallback(async (q, pg) => {
    setLoadingLeads(true);
    try {
      const params = new URLSearchParams({ page: pg, page_size: PAGE_SIZE });
      if (q.trim()) params.set("search", q.trim());
      const data = await adminFetch(`/api/crm/leads/?${params}`);
      setLeads(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [debouncedSearch]);

  useEffect(() => {
    fetchLeads(debouncedSearch, page);
  }, [debouncedSearch, page, fetchLeads]);

  function toggleLead(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (leads.every((l) => selectedIds.has(l.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        leads.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        leads.forEach((l) => next.add(l.id));
        return next;
      });
    }
  }

  const allChecked = leads.length > 0 && leads.every((l) => selectedIds.has(l.id));
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  async function handleReassign() {
    if (!selectedIds.size || !targetAgentId) return;
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const data = await adminFetch("/api/crm/admin/bulk-reassign-leads/", {
        method: "POST",
        body: JSON.stringify({
          lead_ids: Array.from(selectedIds),
          target_agent_id: targetAgentId,
          reason: reason.trim() || "Admin manual reassignment.",
        }),
      });
      setResult(data);
      setSelectedIds(new Set());
      setTargetAgentId("");
      setReason("");
      fetchLeads(search, page);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ras-panel">
      <Notice msg={error} type="error" />
      {result && (
        <div className="message-banner success">
          Reassigned <strong>{result.reassigned}</strong> lead{result.reassigned !== 1 ? "s" : ""} successfully.
        </div>
      )}

      {/* Controls + search — pinned at top */}
      <div className="ras-section ras-controls">
        <div className="ras-controls-top">
          <div className="ras-search-wrap">
            <svg className="ras-search-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" width="15" height="15">
              <circle cx="8.5" cy="8.5" r="5.5" />
              <path d="M14.5 14.5l3 3" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              className="ras-search"
              placeholder="Search leads by name, phone, city, ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ras-controls-fields">
            <label className="field-stack">
              <span>Reassign to agent</span>
              <select value={targetAgentId} onChange={(e) => setTargetAgentId(e.target.value)}>
                <option value="">— Select agent —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.full_name} ({a.username})</option>
                ))}
              </select>
            </label>
            <label className="field-stack ras-reason-field">
              <span>Reason</span>
              <input
                type="text"
                placeholder="Reason for reassignment…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="ras-controls-submit">
              <span className="ras-selected-count">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                className="primary-button"
                disabled={!selectedIds.size || !targetAgentId || submitting}
                onClick={handleReassign}
              >
                {submitting ? <><Spinner />Reassigning...</> : "Reassign Selected"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Leads table — scrollable body, sticky header + footer */}
      <div className="ras-table-wrap">
        <div className="ras-table-scroll">
          <table className="ras-table">
            <thead>
              <tr>
                <th className="ras-col-check">
                  <label className="ras-cb-wrap">
                    <input type="checkbox" className="ras-cb-input" checked={allChecked} onChange={toggleAll} />
                    <span className="ras-cb-box" />
                  </label>
                </th>
                <th>Lead ID</th>
                <th>Name</th>
                <th>Phone</th>
                <th>City</th>
                <th>Assigned To</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingLeads ? (
                <tr><td colSpan={7} className="ras-table-state"><Spinner />Loading leads…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={7} className="ras-table-state">No leads found.</td></tr>
              ) : leads.map((lead) => (
                <tr
                  key={lead.id}
                  className={selectedIds.has(lead.id) ? "ras-row-selected" : ""}
                  onClick={() => toggleLead(lead.id)}
                >
                  <td className="ras-col-check" onClick={(e) => e.stopPropagation()}>
                    <label className="ras-cb-wrap">
                      <input type="checkbox" className="ras-cb-input" checked={selectedIds.has(lead.id)} onChange={() => toggleLead(lead.id)} />
                      <span className="ras-cb-box" />
                    </label>
                  </td>
                  <td className="mono-text">{lead.client_id}</td>
                  <td>{lead.full_name}</td>
                  <td className="mono-text">{lead.phone_number}</td>
                  <td>{lead.city_name || "—"}</td>
                  <td>{lead.assigned_agent?.full_name || lead.assigned_to_name || "Unassigned"}</td>
                  <td>
                    <span className={`ras-status-pill ras-status-${lead.status}`}>
                      {lead.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sticky footer pagination */}
        <div className="ras-table-footer">
          <span className="ras-page-info">{totalCount} lead{totalCount !== 1 ? "s" : ""} total</span>
          <div className="ras-page-controls">
            <button type="button" className="ras-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
            <span className="ras-page-label">Page {page} of {totalPages || 1}</span>
            <button type="button" className="ras-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Root ReassignTab ──────────────────────────────────────────────────────────

const SUB_TABS = ["Reassign Agent Leads", "Reassign Leads"];

export default function ReassignTab() {
  const [activeSubTab, setActiveSubTab] = useState("Reassign Agent Leads");
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  useEffect(() => {
    adminFetch("/api/accounts/users/?role=agent&is_active=true&page_size=200")
      .then((data) =>
        setAgents(
          (data?.results || data || []).filter((agent) =>
            userHasNamedTabAccess(agent, "sales_crm", "sales crm"),
          ),
        ),
      )
      .catch(() => {})
      .finally(() => setLoadingAgents(false));
  }, []);

  return (
    <div className="ras-root">
      <div className="ras-sub-tab-strip">
        {SUB_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`ras-sub-tab-btn${activeSubTab === tab ? " active" : ""}`}
            onClick={() => setActiveSubTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {loadingAgents ? (
        <div className="ras-loading"><Spinner />Loading agents...</div>
      ) : activeSubTab === "Reassign Agent Leads" ? (
        <ReassignAgentLeadsPanel agents={agents} />
      ) : (
        <ReassignLeadsPanel agents={agents} />
      )}
    </div>
  );
}
